import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Card, CardBody, CardHeader } from "../../design-system";
import { borders, surfaces, textColors, typography } from "../../design-system/tokens";
import AffiliatePrimaryButton from "../components/AffiliatePrimaryButton";
import AffiliateSecondaryButton from "../components/AffiliateSecondaryButton";
import {
  useAvailableAccessCoupons,
  useCouponSends,
  useRecipientAccessCounts,
  useSendAccessCoupons,
} from "../hooks/useAffiliateCouponSends";
import {
  ACCESS_COUPON_MIN_PER_RECIPIENT,
  ACCESS_COUPONS_PER_RECIPIENT_TOTAL,
  ACCESS_SEND_MAX_RECIPIENTS_PER_REQUEST,
} from "../utils/couponSendLimits";
import { affiliateBrandBar } from "../utils/uiClasses";

/* ------------------------------------------------------------------ *
 * Pure helpers (exported for unit tests)
 * ------------------------------------------------------------------ */

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[A-Za-z]{2,}$/;

/** Split a pasted blob on comma / semicolon / newline / whitespace. */
export const splitEmailInput = (text) =>
  String(text || "")
    .split(/[\s,;]+/)
    .map((part) => part.trim().toLowerCase())
    .filter(Boolean);

export const isValidEmail = (email) => EMAIL_PATTERN.test(String(email || ""));

const COUNT_OPTIONS = Array.from(
  { length: ACCESS_COUPONS_PER_RECIPIENT_TOTAL - ACCESS_COUPON_MIN_PER_RECIPIENT + 1 },
  (_v, i) => ACCESS_COUPON_MIN_PER_RECIPIENT + i
);

/** Fisher–Yates on a copy — the pool order from the API is not shuffled. */
const shuffled = (ids) => {
  const copy = [...ids];
  for (let i = copy.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
};

/** How many coupons a row should be holding right now. */
const desiredCountFor = (row, blockedEmails) =>
  row.valid && !blockedEmails.has(row.email) ? row.count : 0;

/**
 * The single place where coupons are attached to rows.
 *
 * Pass 1 keeps every assignment that is still in the pool and not already
 * claimed by an earlier row (so manual overrides survive), truncating rows that
 * hold more than they should. Pass 2 tops up the deficits from the shuffled
 * remainder. Because both passes draw from one `taken` set, a coupon can never
 * end up on two rows.
 */
export const syncRowCoupons = (rows, poolIds, blockedEmails = new Set()) => {
  const poolSet = new Set(poolIds);
  const taken = new Set();

  const kept = rows.map((row) => {
    const desired = desiredCountFor(row, blockedEmails);
    const couponIds = [];
    row.couponIds.forEach((id) => {
      if (couponIds.length >= desired) return;
      if (!poolSet.has(id) || taken.has(id)) return;
      taken.add(id);
      couponIds.push(id);
    });
    return couponIds.length === row.couponIds.length && couponIds.every((id, i) => id === row.couponIds[i])
      ? row
      : { ...row, couponIds };
  });

  const free = shuffled(poolIds.filter((id) => !taken.has(id)));
  let cursor = 0;
  return kept.map((row) => {
    const missing = desiredCountFor(row, blockedEmails) - row.couponIds.length;
    if (missing <= 0) return row;
    const extra = free.slice(cursor, cursor + missing);
    cursor += extra.length;
    if (!extra.length) return row;
    return { ...row, couponIds: [...row.couponIds, ...extra] };
  });
};

const sameAssignments = (a, b) =>
  a.length === b.length && a.every((row, i) => row.couponIds.join(",") === b[i].couponIds.join(","));

const makeRow = (email) => ({
  email,
  valid: isValidEmail(email),
  count: ACCESS_COUPON_MIN_PER_RECIPIENT,
  couponIds: [],
});

/** Reasons the backend reports per recipient, in human words. */
const SKIP_REASONS = {
  // Fires whenever already + requested would exceed the total, not only at the
  // ceiling — so it must not claim the address already holds the full amount.
  over_lifetime_limit: `Would exceed the ${ACCESS_COUPONS_PER_RECIPIENT_TOTAL}-coupon limit for this address.`,
  coupon_unavailable: "The chosen coupon was no longer available.",
  invalid_email: "Not a valid e-mail address.",
  duplicate_in_request: "Listed more than once in the same request.",
};

const describeResult = (result) => {
  if (!result?.error) return null;
  return SKIP_REASONS[result.error] || result.error;
};

const formatFreeAccess = (coupon) => {
  const plan = String(coupon?.free_access_plan || "").toUpperCase();
  if (!plan) return null;
  if (coupon.free_access_days) return `${plan} · ${coupon.free_access_days} days`;
  if (coupon.free_access_until) return `${plan} · until ${formatDateShort(coupon.free_access_until)}`;
  return plan;
};

function formatDateShort(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
}

const useIsDesktop = () => {
  const [isDesktop, setIsDesktop] = useState(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
    return window.matchMedia("(min-width: 768px)").matches;
  });

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return undefined;
    const media = window.matchMedia("(min-width: 768px)");
    const onChange = () => setIsDesktop(media.matches);
    onChange();
    if (typeof media.addEventListener === "function") {
      media.addEventListener("change", onChange);
      return () => media.removeEventListener("change", onChange);
    }
    media.addListener(onChange);
    return () => media.removeListener(onChange);
  }, []);

  return isDesktop;
};

/**
 * One half of the send history — either the codes still waiting to be redeemed or
 * the ones already used.
 *
 * `onResend` is only passed for the waiting group: a redeemed code has served its
 * purpose, so chasing it would just confuse the recipient.
 */
const HistoryGroup = ({ title, emptyText, rows, isDesktop, onResend = null, resendingId = null }) => (
  <div className="space-y-2">
    <p className={`${typography.label} ${textColors.secondary}`}>
      {title} ({rows.length})
    </p>
    {rows.length === 0 ? (
      <p className={`${typography.bodySmall} ${textColors.muted}`}>{emptyText}</p>
    ) : isDesktop ? (
      <div className={`${surfaces.primary} rounded-lg overflow-hidden ${borders.default} border`}>
        <div className="overflow-x-auto">
          <table className="min-w-full w-full" aria-label={`${title} access coupons table`}>
            <thead className={`${surfaces.secondary} ${borders.default} border-b`}>
              <tr>
                <th className={`px-4 py-3 text-left ${typography.label} ${textColors.secondary}`}>
                  E-mail
                </th>
                <th className={`px-4 py-3 text-left ${typography.label} ${textColors.secondary}`}>
                  Code
                </th>
                <th className={`px-4 py-3 text-left ${typography.label} ${textColors.secondary}`}>
                  Status
                </th>
                <th className={`px-4 py-3 text-left ${typography.label} ${textColors.secondary}`}>
                  Sent
                </th>
                {onResend ? (
                  <th className={`px-4 py-3 text-right ${typography.label} ${textColors.secondary}`}>
                    Actions
                  </th>
                ) : null}
              </tr>
            </thead>
            <tbody>
              {rows.map((entry) => (
                <tr key={entry.id} className={`${borders.default} border-b`}>
                  <td className={`px-4 py-3 ${typography.body} ${textColors.primary} break-all`}>
                    {entry.recipient_email}
                  </td>
                  <td className={`px-4 py-3 ${typography.bodySmall} ${textColors.primary} break-all`}>
                    {entry.coupon_code}
                  </td>
                  <td className="px-4 py-3">
                    <StatusPill status={entry.status} />
                  </td>
                  <td className={`px-4 py-3 ${typography.bodySmall} ${textColors.secondary}`}>
                    {formatDateShort(entry.created_at)}
                  </td>
                  {onResend ? (
                    <td className="px-4 py-3 text-right">
                      <AffiliateSecondaryButton
                        aria-label={`Send ${entry.coupon_code} to ${entry.recipient_email} again`}
                        disabled={resendingId === entry.id}
                        onClick={() => onResend(entry)}
                      >
                        {resendingId === entry.id ? "Sending…" : "Send again"}
                      </AffiliateSecondaryButton>
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    ) : (
      <div className="space-y-3" aria-label={`${title} access coupons list`}>
        {rows.map((entry) => (
          <div
            key={entry.id}
            className={`${surfaces.primary} rounded-xl border ${borders.default} p-4 space-y-2`}
          >
            <p className={`${typography.body} ${textColors.primary} font-medium break-all`}>
              {entry.recipient_email}
            </p>
            <p className={`${typography.bodySmall} ${textColors.primary} break-all`}>
              {entry.coupon_code}
            </p>
            <div className="flex items-center gap-2">
              <StatusPill status={entry.status} />
              <span className={`${typography.caption} ${textColors.muted}`}>
                {formatDateShort(entry.created_at)}
              </span>
            </div>
            {onResend ? (
              <AffiliateSecondaryButton
                fullWidth
                aria-label={`Send ${entry.coupon_code} to ${entry.recipient_email} again`}
                disabled={resendingId === entry.id}
                onClick={() => onResend(entry)}
              >
                {resendingId === entry.id ? "Sending…" : "Send again"}
              </AffiliateSecondaryButton>
            ) : null}
          </div>
        ))}
      </div>
    )}
  </div>
);

/* ------------------------------------------------------------------ *
 * Row pieces (shared by the mobile cards and the desktop table)
 * ------------------------------------------------------------------ */

const RowStatus = ({ row, alreadyCount }) => {
  if (!row.valid) {
    return (
      <span className={`${typography.bodySmall} text-red-600 dark:text-red-400`}>
        Invalid e-mail address — fix or remove it.
      </span>
    );
  }
  if (alreadyCount >= ACCESS_COUPONS_PER_RECIPIENT_TOTAL) {
    return (
      <span className={`${typography.bodySmall} text-red-600 dark:text-red-400`}>
        Already received {alreadyCount} access coupons — the limit is{" "}
        {ACCESS_COUPONS_PER_RECIPIENT_TOTAL}, so this address cannot be sent to.
      </span>
    );
  }
  if (alreadyCount > 0) {
    return (
      <span className={`${typography.bodySmall} text-amber-700 dark:text-amber-400`}>
        Already received {alreadyCount} access coupon{alreadyCount === 1 ? "" : "s"} —{" "}
        {ACCESS_COUPONS_PER_RECIPIENT_TOTAL - alreadyCount} left.
      </span>
    );
  }
  return <span className={`${typography.bodySmall} ${textColors.muted}`}>Ready to send</span>;
};

/**
 * How many more coupons this address may still receive.
 *
 * The backend rejects a recipient outright when `already + requested` exceeds the
 * lifetime total — it does not send a partial batch — so the selector must never
 * offer a number that would get the whole recipient skipped.
 */
export const remainingAllowanceFor = (alreadyCount) =>
  Math.max(0, ACCESS_COUPONS_PER_RECIPIENT_TOTAL - Number(alreadyCount || 0));

const CountSelect = ({ row, alreadyCount = 0, disabled, onChange }) => {
  const remaining = remainingAllowanceFor(alreadyCount);
  const options = COUNT_OPTIONS.filter((value) => value <= remaining);
  return (
    <select
      aria-label={`Coupon count for ${row.email}`}
      className={`px-2 py-1 rounded-md border ${borders.default} ${surfaces.primary} ${textColors.primary} ${typography.bodySmall}`}
      value={row.count}
      disabled={disabled || options.length === 0}
      onChange={(event) => onChange(row.email, Number(event.target.value))}
    >
      {options.map((value) => (
        <option key={value} value={value}>
          {value}
        </option>
      ))}
    </select>
  );
};

const CouponCell = ({ row, couponsById, freeCoupons, picker, onOpenPicker, onPick, onClosePicker }) => {
  if (!row.couponIds.length) {
    return <span className={`${typography.bodySmall} ${textColors.muted}`}>—</span>;
  }
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1.5">
        {row.couponIds.map((couponId, index) => {
          const coupon = couponsById.get(couponId);
          const code = coupon?.code || String(couponId);
          const isOpen = picker && picker.email === row.email && picker.index === index;
          return (
            <button
              key={couponId}
              type="button"
              aria-label={`Change coupon ${code} for ${row.email}`}
              title={formatFreeAccess(coupon) || "Pick a different coupon"}
              onClick={() => (isOpen ? onClosePicker() : onOpenPicker(row.email, index))}
              className={`px-2 py-1 rounded-md border ${borders.default} ${
                isOpen ? surfaces.secondary : surfaces.primary
              } ${surfaces.hover} ${typography.bodySmall} ${textColors.primary} font-medium break-all`}
            >
              {code}
            </button>
          );
        })}
      </div>

      {picker && picker.email === row.email ? (
        <div
          className={`rounded-lg border ${borders.default} ${surfaces.secondary} p-2 space-y-2`}
          aria-label={`Available coupons for ${row.email}`}
        >
          {freeCoupons.length ? (
            <div className="flex flex-wrap gap-1.5">
              {freeCoupons.map((coupon) => (
                <button
                  key={coupon.id}
                  type="button"
                  aria-label={`Use coupon ${coupon.code} for ${row.email}`}
                  title={formatFreeAccess(coupon) || "Use this coupon"}
                  onClick={() => onPick(row.email, picker.index, coupon.id)}
                  className={`px-2 py-1 rounded-md border ${borders.default} ${surfaces.primary} ${surfaces.hover} ${typography.bodySmall} ${textColors.primary} break-all`}
                >
                  {coupon.code}
                </button>
              ))}
            </div>
          ) : (
            <p className={`${typography.caption} ${textColors.muted}`}>
              No other coupon is free right now.
            </p>
          )}
          <button
            type="button"
            onClick={onClosePicker}
            className={`${typography.caption} ${textColors.secondary} underline`}
          >
            Cancel
          </button>
        </div>
      ) : null}
    </div>
  );
};

const StatusPill = ({ status }) => {
  const styles = {
    sent: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300",
    skipped: "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300",
    failed: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300",
  };
  return (
    <span className={`inline-block px-2 py-0.5 rounded-full ${typography.caption} ${styles[status] || ""}`}>
      {status}
    </span>
  );
};

/* ------------------------------------------------------------------ *
 * Page
 * ------------------------------------------------------------------ */

const SendAccessCouponsPage = () => {
  const [pasteValue, setPasteValue] = useState("");
  const [rows, setRows] = useState([]);
  const [picker, setPicker] = useState(null); // { email, index }
  const [sendResponse, setSendResponse] = useState(null);
  const isDesktop = useIsDesktop();

  const availableQuery = useAvailableAccessCoupons();
  const historyQuery = useCouponSends({ kind: "access", limit: 50, offset: 0 });
  const sendMutation = useSendAccessCoupons();
  // "Send again" is a reminder to someone who already holds the code; the backend
  // reuses their existing row, so it consumes no further usage.
  const [resendingId, setResendingId] = useState(null);
  const [resendFeedback, setResendFeedback] = useState(null); // { type, message }
  // History controls: free-text email search + which half to show.
  const [historySearch, setHistorySearch] = useState("");
  const [historyFilter, setHistoryFilter] = useState("all"); // all | awaiting | redeemed

  const pool = useMemo(() => availableQuery.data?.available || [], [availableQuery.data]);
  const poolIds = useMemo(() => pool.map((coupon) => coupon.id), [pool]);
  const couponsById = useMemo(() => new Map(pool.map((coupon) => [coupon.id, coupon])), [pool]);

  const validEmails = useMemo(() => rows.filter((row) => row.valid).map((row) => row.email), [rows]);
  const countsQuery = useRecipientAccessCounts(validEmails);
  const recipientCounts = useMemo(() => countsQuery.data?.counts || {}, [countsQuery.data]);
  const blockedEmails = useMemo(
    () =>
      new Set(
        Object.entries(recipientCounts)
          .filter(([, count]) => Number(count) >= ACCESS_COUPONS_PER_RECIPIENT_TOTAL)
          .map(([email]) => String(email).trim().toLowerCase())
      ),
    [recipientCounts]
  );

  // The pool arrives after the first render, and the lifetime counts after
  // that; both change what each row should be holding. Counts arriving late can
  // also invalidate a count the sender already picked (they chose 3 before we
  // knew the address already had 2), so clamp to the remaining allowance first —
  // the backend skips the whole recipient when already + requested exceeds the
  // total, so an unclamped row would silently deliver nothing.
  useEffect(() => {
    setRows((prev) => {
      const clamped = prev.map((row) => {
        const allowance = remainingAllowanceFor(recipientCounts[row.email]);
        if (allowance <= 0 || row.count <= allowance) return row;
        return { ...row, count: allowance };
      });
      const next = syncRowCoupons(clamped, poolIds, blockedEmails);
      const unchanged =
        sameAssignments(prev, next) && prev.every((row, i) => row.count === next[i].count);
      return unchanged ? prev : next;
    });
  }, [poolIds, blockedEmails, recipientCounts]);

  const handleResend = useCallback(
    async (entry) => {
      if (!entry?.coupon_id || resendingId) return;
      setResendingId(entry.id);
      setResendFeedback(null);
      try {
        const data = await sendMutation.mutateAsync([
          { email: entry.recipient_email, coupon_ids: [entry.coupon_id] },
        ]);
        const result = data?.results?.[0] || null;
        if (result && result.status !== "sent") {
          setResendFeedback({
            type: "error",
            message: `Could not resend to ${entry.recipient_email}: ${
              describeResult(result) || result.status
            }`,
          });
          return;
        }
        setResendFeedback({
          type: "success",
          message: `${entry.coupon_code} sent to ${entry.recipient_email} again.`,
        });
      } catch (error) {
        setResendFeedback({
          type: "error",
          message: error?.message || "Could not resend this coupon.",
        });
      } finally {
        setResendingId(null);
      }
    },
    [resendingId, sendMutation]
  );

  const addEmailsFromText = useCallback(
    (text) => {
      const parsed = splitEmailInput(text);
      if (!parsed.length) return;
      setRows((prev) => {
        const seen = new Set(prev.map((row) => row.email));
        const added = [];
        parsed.forEach((email) => {
          if (seen.has(email)) return; // case-insensitive dedupe: emails are lowercased
          seen.add(email);
          added.push(makeRow(email));
        });
        if (!added.length) return prev;
        return syncRowCoupons([...prev, ...added], poolIds, blockedEmails);
      });
    },
    [poolIds, blockedEmails]
  );

  const handlePaste = (event) => {
    const text = event.clipboardData?.getData?.("text");
    if (text) {
      event.preventDefault();
      setPasteValue("");
      addEmailsFromText(text);
      return;
    }
    // No clipboard payload available (some browsers/tests): parse whatever the
    // textarea ends up holding once the paste has been applied.
    const element = event.currentTarget;
    window.setTimeout(() => {
      addEmailsFromText(element.value);
      setPasteValue("");
    }, 0);
  };

  const handleBlur = () => {
    if (!pasteValue.trim()) return;
    addEmailsFromText(pasteValue);
    setPasteValue("");
  };

  const handleAddClick = () => {
    addEmailsFromText(pasteValue);
    setPasteValue("");
  };

  const handleCountChange = (email, count) => {
    setPicker(null);
    setRows((prev) =>
      syncRowCoupons(
        prev.map((row) => (row.email === email ? { ...row, count } : row)),
        poolIds,
        blockedEmails
      )
    );
  };

  const handleRemove = (email) => {
    setPicker(null);
    setRows((prev) => syncRowCoupons(prev.filter((row) => row.email !== email), poolIds, blockedEmails));
  };

  const handlePickCoupon = (email, index, couponId) => {
    setPicker(null);
    setRows((prev) =>
      syncRowCoupons(
        prev.map((row) => {
          if (row.email !== email) return row;
          const couponIds = [...row.couponIds];
          couponIds[index] = couponId;
          return { ...row, couponIds };
        }),
        poolIds,
        blockedEmails
      )
    );
  };

  const assignedIds = useMemo(() => new Set(rows.flatMap((row) => row.couponIds)), [rows]);
  const freeCoupons = useMemo(
    () => pool.filter((coupon) => !assignedIds.has(coupon.id)),
    [pool, assignedIds]
  );

  const sendableRows = useMemo(
    () => rows.filter((row) => row.valid && !blockedEmails.has(row.email) && row.couponIds.length > 0),
    [rows, blockedEmails]
  );
  const requestedTotal = useMemo(
    () => rows.filter((row) => row.valid && !blockedEmails.has(row.email)).reduce((sum, row) => sum + row.count, 0),
    [rows, blockedEmails]
  );
  const invalidCount = rows.filter((row) => !row.valid).length;
  const blockedCount = rows.filter((row) => row.valid && blockedEmails.has(row.email)).length;

  const exceedsPool = requestedTotal > poolIds.length;
  const exceedsRequestCap = sendableRows.length > ACCESS_SEND_MAX_RECIPIENTS_PER_REQUEST;
  const blockingMessage = exceedsPool
    ? `You asked for ${requestedTotal} coupon${requestedTotal === 1 ? "" : "s"} but only ${
        poolIds.length
      } ${poolIds.length === 1 ? "is" : "are"} available. Lower the counts, remove recipients, or buy more coupons.`
    : exceedsRequestCap
      ? `You can send to at most ${ACCESS_SEND_MAX_RECIPIENTS_PER_REQUEST} recipients per send — remove ${
          sendableRows.length - ACCESS_SEND_MAX_RECIPIENTS_PER_REQUEST
        } of them and send the rest afterwards.`
      : null;
  const canSend = sendableRows.length > 0 && !blockingMessage && !sendMutation.isPending;

  const handleSend = () => {
    if (!canSend) return;
    const recipients = sendableRows.map((row) => ({ email: row.email, coupon_ids: row.couponIds }));
    setPicker(null);
    sendMutation.mutate(recipients, {
      onSuccess: (data) => {
        setSendResponse(data);
        const sentEmails = new Set(
          (data?.results || []).filter((r) => r.status === "sent").map((r) => String(r.email).toLowerCase())
        );
        if (sentEmails.size) {
          setRows((prev) => prev.filter((row) => !sentEmails.has(row.email)));
        }
      },
    });
  };

  const historyRows = historyQuery.data?.results || [];
  // Search narrows by address (and code, which is free and often what you have to
  // hand); the filter picks which half to show.
  const historyNeedle = historySearch.trim().toLowerCase();
  const matchedHistoryRows = historyNeedle
    ? historyRows.filter(
        (row) =>
          String(row.recipient_email || "").toLowerCase().includes(historyNeedle) ||
          String(row.coupon_code || "").toLowerCase().includes(historyNeedle)
      )
    : historyRows;
  // Split the history: codes actually used vs ones still sitting unredeemed in an
  // inbox. Only the latter are worth chasing, so only they get "Send again".
  const redeemedRows = matchedHistoryRows.filter((row) => row.coupon_redeemed);
  const awaitingRows = matchedHistoryRows.filter((row) => !row.coupon_redeemed);
  const showAwaiting = historyFilter === "all" || historyFilter === "awaiting";
  const showRedeemed = historyFilter === "all" || historyFilter === "redeemed";

  const renderRecipientsTable = () => (
    <div className={`${surfaces.primary} rounded-lg overflow-hidden ${borders.default} border`}>
      <div className="overflow-x-auto">
        <table className="min-w-full w-full" aria-label="Recipients table">
          <thead className={`${surfaces.secondary} ${borders.default} border-b`}>
            <tr>
              <th className={`px-4 py-3 text-left ${typography.label} ${textColors.secondary}`}>E-mail</th>
              <th className={`px-4 py-3 text-left ${typography.label} ${textColors.secondary}`}>Coupons</th>
              <th className={`px-4 py-3 text-left ${typography.label} ${textColors.secondary}`}>Codes</th>
              <th className={`px-4 py-3 text-left ${typography.label} ${textColors.secondary}`}>Status</th>
              <th className={`px-4 py-3 text-right ${typography.label} ${textColors.secondary}`}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.email} className={`${borders.default} border-b align-top`}>
                <td className={`px-4 py-3 ${typography.body} ${textColors.primary} break-all`}>{row.email}</td>
                <td className="px-4 py-3">
                  <CountSelect
                    row={row}
                    alreadyCount={Number(recipientCounts[row.email] || 0)}
                    disabled={!row.valid}
                    onChange={handleCountChange}
                  />
                </td>
                <td className="px-4 py-3">
                  <CouponCell
                    row={row}
                    couponsById={couponsById}
                    freeCoupons={freeCoupons}
                    picker={picker}
                    onOpenPicker={(email, index) => setPicker({ email, index })}
                    onClosePicker={() => setPicker(null)}
                    onPick={handlePickCoupon}
                  />
                </td>
                <td className="px-4 py-3">
                  <RowStatus row={row} alreadyCount={Number(recipientCounts[row.email] || 0)} />
                </td>
                <td className="px-4 py-3 text-right">
                  <button
                    type="button"
                    aria-label={`Remove ${row.email}`}
                    onClick={() => handleRemove(row.email)}
                    className={`${typography.bodySmall} ${textColors.secondary} underline`}
                  >
                    Remove
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderRecipientsCards = () => (
    <div className="space-y-3" aria-label="Recipients list">
      {rows.map((row) => (
        <div
          key={row.email}
          className={`${surfaces.primary} rounded-xl border ${borders.default} p-4 space-y-3`}
        >
          <p className={`${typography.body} ${textColors.primary} font-medium break-all`}>{row.email}</p>
          <div className="flex items-center gap-3">
            <span className={`${typography.caption} ${textColors.muted}`}>Coupons</span>
            <CountSelect
                    row={row}
                    alreadyCount={Number(recipientCounts[row.email] || 0)}
                    disabled={!row.valid}
                    onChange={handleCountChange}
                  />
          </div>
          <CouponCell
            row={row}
            couponsById={couponsById}
            freeCoupons={freeCoupons}
            picker={picker}
            onOpenPicker={(email, index) => setPicker({ email, index })}
            onClosePicker={() => setPicker(null)}
            onPick={handlePickCoupon}
          />
          <RowStatus row={row} alreadyCount={Number(recipientCounts[row.email] || 0)} />
          <button
            type="button"
            aria-label={`Remove ${row.email}`}
            onClick={() => handleRemove(row.email)}
            className={`${typography.bodySmall} ${textColors.secondary} underline`}
          >
            Remove
          </button>
        </div>
      ))}
    </div>
  );

  return (
    <div className="p-4 sm:p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        <div>
          {/* Matches the "Send Coupons" nav tab — a heading that disagrees with the
              tab the user just clicked reads like the wrong page loaded. */}
          <h1 className={`${typography.h2} ${textColors.primary}`}>Send Coupons</h1>
          <p className={`${typography.body} ${textColors.secondary} mt-1`}>
            Paste the e-mail addresses you want to gift free access to. Each recipient gets up to{" "}
            {ACCESS_COUPONS_PER_RECIPIENT_TOTAL} access coupons, ever.
          </p>
        </div>

        <div
          className={`relative overflow-hidden rounded-2xl border ${borders.glass} ${surfaces.glassCard} p-5 shadow-sm`}
        >
          <div className={`absolute inset-x-0 top-0 h-1 ${affiliateBrandBar}`} />
          <p className={`${typography.label} ${textColors.secondary}`}>Available access coupons</p>
          <p className={`mt-1 ${typography.h3} ${textColors.primary}`}>
            {availableQuery.isLoading ? "…" : poolIds.length}
          </p>
          <p className={`mt-1 ${typography.caption} ${textColors.muted}`}>
            {requestedTotal} requested for {rows.length} pasted address{rows.length === 1 ? "" : "es"}
          </p>
        </div>

        <Card>
          <CardHeader
            title="Recipients"
            subtitle="Separate addresses with commas, semicolons, spaces or new lines. Pasting again adds to the list."
          />
          <CardBody className="space-y-4">
            <div className="space-y-2">
              <label
                htmlFor="access-coupon-emails"
                className={`${typography.label} ${textColors.secondary} block`}
              >
                E-mail addresses
              </label>
              <textarea
                id="access-coupon-emails"
                rows={4}
                value={pasteValue}
                placeholder="ana@example.com, bruno@example.com; carla@example.com"
                onChange={(event) => setPasteValue(event.target.value)}
                onPaste={handlePaste}
                onBlur={handleBlur}
                className={`w-full px-3 py-2 rounded-lg border ${borders.default} ${surfaces.primary} ${textColors.primary} ${typography.bodySmall}`}
              />
              <div className="flex flex-wrap items-center gap-3">
                <AffiliatePrimaryButton type="button" size="sm" onClick={handleAddClick}>
                  Add addresses
                </AffiliatePrimaryButton>
                <span className={`${typography.caption} ${textColors.muted}`}>
                  {rows.length} recipient{rows.length === 1 ? "" : "s"}
                  {invalidCount ? ` · ${invalidCount} invalid` : ""}
                  {blockedCount ? ` · ${blockedCount} at the lifetime limit` : ""}
                </span>
              </div>
            </div>

            {availableQuery.error ? (
              <p className={`${typography.bodySmall} text-red-600 dark:text-red-400`}>
                Failed to load your available coupons:{" "}
                {availableQuery.error?.message || "Unknown error"}
              </p>
            ) : null}

            {rows.length === 0 ? (
              <p className={`${typography.bodySmall} ${textColors.secondary}`}>
                No recipients yet — paste some addresses above.
              </p>
            ) : isDesktop ? (
              renderRecipientsTable()
            ) : (
              renderRecipientsCards()
            )}

            {rows.length > 0 ? (
              <div className="space-y-2">
                {blockingMessage ? (
                  <p
                    role="alert"
                    className={`${typography.bodySmall} text-red-600 dark:text-red-400`}
                  >
                    {blockingMessage}
                  </p>
                ) : null}
                {invalidCount ? (
                  <p className={`${typography.caption} ${textColors.muted}`}>
                    {invalidCount} invalid address{invalidCount === 1 ? "" : "es"} will not be sent.
                  </p>
                ) : null}
                {sendMutation.error ? (
                  <p className={`${typography.bodySmall} text-red-600 dark:text-red-400`}>
                    {sendMutation.error?.message || "Failed to send."}
                  </p>
                ) : null}
                <AffiliatePrimaryButton
                  type="button"
                  onClick={handleSend}
                  disabled={!canSend}
                  loading={sendMutation.isPending}
                >
                  {`Send to ${sendableRows.length} recipient${sendableRows.length === 1 ? "" : "s"}`}
                </AffiliatePrimaryButton>
              </div>
            ) : null}

            {sendResponse ? (
              <div className={`rounded-xl border ${borders.default} ${surfaces.secondary} p-4 space-y-3`}>
                <h3 className={`${typography.h5} ${textColors.primary}`}>Send results</h3>
                <p className={`${typography.bodySmall} ${textColors.secondary}`}>
                  {sendResponse.sent_count ?? 0} sent · {sendResponse.skipped_count ?? 0} skipped ·{" "}
                  {sendResponse.failed_count ?? 0} failed
                </p>
                <ul className="space-y-2" aria-label="Send results">
                  {(sendResponse.results || []).map((result) => (
                    <li
                      key={result.email}
                      className={`${typography.bodySmall} ${textColors.primary} flex flex-wrap items-center gap-2`}
                    >
                      <span className="break-all font-medium">{result.email}</span>
                      <StatusPill status={result.status} />
                      {result.coupon_codes?.length ? (
                        <span className={`${textColors.secondary} break-all`}>
                          {result.coupon_codes.join(", ")}
                        </span>
                      ) : null}
                      {describeResult(result) ? (
                        <span className={textColors.muted}>{describeResult(result)}</span>
                      ) : null}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </CardBody>
        </Card>

        <Card>
          <CardHeader
            title="Already sent"
            subtitle="Split by whether the person has actually redeemed the code yet."
          />
          <CardBody className="space-y-4">
            {historyQuery.isLoading ? (
              <p className={`${typography.bodySmall} ${textColors.secondary}`}>Loading…</p>
            ) : null}
            {historyQuery.error ? (
              <p className={`${typography.bodySmall} text-red-600 dark:text-red-400`}>
                Failed to load history: {historyQuery.error?.message || "Unknown error"}
              </p>
            ) : null}
            {!historyQuery.isLoading && !historyQuery.error && historyRows.length === 0 ? (
              <p className={`${typography.bodySmall} ${textColors.secondary}`}>
                You have not sent any access coupons yet.
              </p>
            ) : null}

            {historyRows.length > 0 ? (
              <>
                <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                  <div className="flex-1">
                    <label htmlFor="history-search" className="sr-only">
                      Search sent coupons by e-mail
                    </label>
                    <input
                      id="history-search"
                      type="search"
                      value={historySearch}
                      onChange={(e) => setHistorySearch(e.target.value)}
                      placeholder="Search by e-mail or code…"
                      aria-label="Search sent coupons by e-mail"
                      className={[
                        "w-full px-3 py-2 rounded-lg border",
                        surfaces.primary,
                        borders.default,
                        typography.bodySmall,
                        textColors.primary,
                        "focus:outline-none focus:ring-2 focus:ring-pink-400 dark:focus:ring-pink-300",
                      ].join(" ")}
                    />
                  </div>
                  <div className="flex items-center gap-2" role="group" aria-label="Filter sent coupons by status">
                    {[
                      { value: "all", label: "All" },
                      { value: "awaiting", label: "Sent" },
                      { value: "redeemed", label: "Used" },
                    ].map((option) => {
                      const isSelected = historyFilter === option.value;
                      return (
                        <button
                          key={option.value}
                          type="button"
                          onClick={() => setHistoryFilter(option.value)}
                          aria-pressed={isSelected}
                          className={[
                            "px-2.5 py-1 rounded-full border",
                            typography.caption,
                            isSelected
                              ? "bg-purple-100 dark:bg-purple-900/30 border-purple-300 dark:border-purple-700 text-purple-800 dark:text-purple-200"
                              : `${surfaces.primary} ${borders.default} ${textColors.secondary}`,
                          ].join(" ")}
                        >
                          {option.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {matchedHistoryRows.length === 0 ? (
                  <p className={`${typography.bodySmall} ${textColors.secondary}`}>
                    No sent coupons match that search.
                  </p>
                ) : null}

                {showAwaiting ? (
                  <HistoryGroup
                    title="Waiting to be used"
                    emptyText="Everyone you sent to has redeemed their code."
                    rows={awaitingRows}
                    isDesktop={isDesktop}
                    onResend={handleResend}
                    resendingId={resendingId}
                  />
                ) : null}
                {showRedeemed ? (
                  <HistoryGroup
                    title="Redeemed"
                    emptyText="Nobody has redeemed a code yet."
                    rows={redeemedRows}
                    isDesktop={isDesktop}
                  />
                ) : null}
              </>
            ) : null}

            {resendFeedback ? (
              <p
                role={resendFeedback.type === "error" ? "alert" : "status"}
                className={`${typography.bodySmall} ${
                  resendFeedback.type === "error"
                    ? "text-red-600 dark:text-red-400"
                    : "text-green-700 dark:text-green-400"
                }`}
              >
                {resendFeedback.message}
              </p>
            ) : null}
          </CardBody>
        </Card>
      </div>
    </div>
  );
};

export default SendAccessCouponsPage;
